import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErroApi, rotas } from '@shared/lib/api';
import type { EuResposta } from '@shared/types/api';
import { authConfigurado, supabase } from './supabase';
import type { Contexto, Usuario } from './perfil';
import { contextoPadrao } from './perfil';

/**
 * Sessao do usuario e contexto ativo.
 *
 * Duas camadas, de proposito:
 *   1. Supabase Auth diz QUEM voce e (o JWT).
 *   2. /api/auth/me diz O QUE voce e aqui dentro (papeis e contextos),
 *      derivado de public.users.
 *
 * O front nao recalcula permissao: os contextos vem prontos do servidor.
 * E nada disto autoriza coisa alguma -- quem barra acesso e a API, em
 * todo request (api/auth.py: exige_admin). Esconder um menu nao protege
 * dado nenhum.
 */

const CHAVE_CONTEXTO = 'copy:contexto';

function chaveDo(c: Contexto) {
  return `${c.modulo}:${c.gestorId ?? ''}`;
}

function paraUsuario(r: EuResposta): Usuario {
  return {
    id: r.id,
    nome: r.nome,
    email: r.email,
    tipo: r.tipo,
    eEstrategista: r.e_estrategista,
    eGestor: r.e_gestor,
    eAdmin: r.e_admin,
    status: r.status,
    contextos: r.contextos.map((c) => ({
      modulo: c.modulo,
      rotulo: c.rotulo,
      gestorId: c.gestor_id ?? undefined,
    })),
  };
}

interface ValorSessao {
  usuario: Usuario | null;
  contexto: Contexto | null;
  trocarContexto: (c: Contexto) => void;
  sair: () => Promise<void>;
  carregando: boolean;
  /** Sem sessao valida: o app deve mostrar o login. */
  precisaLogin: boolean;
  erro: Error | null;
}

const Ctx = createContext<ValorSessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [temToken, setTemToken] = useState<boolean | null>(
    authConfigurado ? null : true,   // sem Supabase, a API responde em modo aberto
  );

  // Ja soubemos, em algum momento, quem e o usuario?
  //
  // `useRef` e nao estado: mudar isto nao precisa re-renderizar, e o
  // valor tem de sobreviver a qualquer re-avaliacao. Serve para NAO
  // regredir a tela: depois da primeira resposta de /auth/me, uma
  // revalidacao nunca mais mostra "Verificando sessao".
  const jaResolvido = useRef(false);

  // Quem estava logado na ultima notificacao do Supabase. Serve para
  // separar "logou agora" de "a aba voltou ao foco".
  const usuarioDaSessao = useRef<string | null>(null);

  // Acompanha a sessao do Supabase: login, logout e refresh de token.
  //
  // Cada evento merece um tratamento DIFERENTE, e confundi-los custa caro:
  //
  //   SIGNED_OUT       limpa tudo -- nada do usuario anterior pode sobrar.
  //   SIGNED_IN        `reset` na sessao: havia um 401 em cache de antes
  //                    do login, e com retry:false ele nao sairia de la
  //                    sozinho. Aqui descartar e necessario.
  //   TOKEN_REFRESHED  NAO descarta nada. E o mesmo usuario, com um token
  //                    novo; o dado continua valido. Um `reset` aqui
  //                    zerava a query e a tela voltava para "Verificando
  //                    sessao" toda vez que a aba recuperava o foco --
  //                    porque o Supabase renova o token nesse momento.
  useEffect(() => {
    if (!authConfigurado) return;
    supabase.auth.getSession().then(({ data }) => {
      setTemToken(!!data.session);
      usuarioDaSessao.current = data.session?.user?.id ?? null;
    });

    const { data: sub } = supabase.auth.onAuthStateChange((evento, sessao) => {
      setTemToken(!!sessao);
      const id = sessao?.user?.id ?? null;
      const trocouDeUsuario = id !== usuarioDaSessao.current;
      usuarioDaSessao.current = id;
      // Outra pessoa (ou ninguem): o que se sabia do usuario anterior nao
      // vale para ela, e ate /auth/me responder a tela e "Verificando".
      if (trocouDeUsuario) jaResolvido.current = false;

      if (evento === 'SIGNED_OUT') {
        qc.clear();
        return;
      }

      // ATENCAO: o Supabase emite SIGNED_IN toda vez que a aba recupera o
      // FOCO, nao so quando alguem loga de verdade. Tratar todo SIGNED_IN
      // como login novo fazia o app invalidar o cache e rebuscar
      // /auth/me a cada troca de aba.
      //
      // O que distingue um do outro e o USUARIO: se e o mesmo de antes,
      // foi so a sessao sendo reemitida e nada precisa ser refeito.
      if (evento === 'SIGNED_IN' && trocouDeUsuario) {
        void qc.resetQueries({ queryKey: ['auth', 'me'] });
        void qc.invalidateQueries();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const { data, isPending, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<EuResposta>(rotas.eu),
    enabled: temToken === true,
    retry: false,
    staleTime: 5 * 60_000,
    // Quem sou eu nao muda ao trocar de aba. O refetch no foco faz
    // sentido para dados vivos (frota, operacao), nao para a sessao --
    // aqui so gera trafego e risco de piscar a interface.
    refetchOnWindowFocus: false,
  });

  const usuario = useMemo(() => (data ? paraUsuario(data) : null), [data]);

  // So o USUARIO resolve, nao a falta de sessao. Marcado tambem sem
  // token, o login deixava um intervalo sem usuario e sem "carregando"
  // ate /auth/me responder -- e o Protegido, sem modulo para o usuario
  // nulo, mostrava "Sem acesso" antes de redirecionar (13/10/2026).
  if (usuario) jaResolvido.current = true;

  const [escolhido, setEscolhido] = useState<string | null>(() => {
    try { return localStorage.getItem(CHAVE_CONTEXTO); } catch { return null; }
  });

  const contexto = useMemo(() => {
    if (!usuario) return null;
    return usuario.contextos.find((c) => chaveDo(c) === escolhido)
        ?? contextoPadrao(usuario);
  }, [usuario, escolhido]);

  const erroApi = error instanceof ErroApi ? error : null;

  const valor = useMemo<ValorSessao>(() => ({
    usuario,
    contexto,
    // 401 da API tambem cai no login: token expirado ou invalido.
    precisaLogin: temToken === false || !!erroApi?.precisaLogin,
    // So na PRIMEIRA carga. Depois que /auth/me respondeu uma vez, a tela
    // nunca mais regride para "Verificando sessao" -- nem quando o
    // Supabase reemite o estado ao recuperar o foco da aba, nem numa
    // revalidacao em segundo plano.
    //
    // Isto importa porque os modulos entram por `lazy`: voltar ao estado
    // de carregamento desmonta o chunk ja carregado e o React precisa
    // resolver o import de novo, piscando a interface inteira.
    carregando: !jaResolvido.current
                && (temToken === null || (temToken === true && isPending)),
    erro: (error as Error) ?? null,
    trocarContexto: (c) => {
      const k = chaveDo(c);
      setEscolhido(k);
      try { localStorage.setItem(CHAVE_CONTEXTO, k); } catch { /* modo privado */ }
    },
    sair: async () => {
      try { localStorage.removeItem(CHAVE_CONTEXTO); } catch { /* ignora */ }
      if (authConfigurado) await supabase.auth.signOut();
      qc.clear();
    },
  }), [usuario, contexto, temToken, isPending, error, erroApi, qc]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSessao(): ValorSessao {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSessao fora do ProvedorSessao');
  return v;
}
